// components/DeploymentLogsGrid.js
import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, styled } from "@mui/material";
import { ref, onValue } from "firebase/database";
import { db } from "../../firebase"; // Adjust the import path as needed
import DeploymentLogsDialog from "./DeploymentLogsModal";

const StyledDataGrid = styled(DataGrid)({
  backgroundColor: "#247BA0",
  border: "1px solid #ddd",
  borderRadius: "4px",
  "& .MuiDataGrid-columnHeaders": {
    background: "linear-gradient(to right, #FF8300, #FF4700)", // Apply the Zenzero gradient across the header
    color: "white",
    fontWeight: "bold",
    height: "10px",
  },
  "& .MuiDataGrid-cell": {
    color: "#333",
  },
});

const DeploymentLogsGrid = ({ searchTerm }) => {
  const [logs, setLogs] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMachineId, setSelectedMachineId] = useState(null);

  // Zenzero color palette
  const zenzeroColors = {
    primary: "#FF8300",
    secondary: "#FF4700",
    action: "#06AEF4", // For action icons/buttons
    cancel: "#9B27B0",
  };
  const zenzeroOrange = "#FF8200";
  const iceWhite = "#FDFEFE";
  // const BlueSteel = "#164E65";
  const Oceania = "#247BA0";

  useEffect(() => {
    const logsRef = ref(db, "deploymentLogs");
    const unsubscribe = onValue(logsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const { formattedData, appColumns } = formatData(data);
        setLogs(formattedData);
        setColumns(appColumns);
      } else {
        setLogs([]);
      }
      setLoading(false);
    });

    return () => unsubscribe(); // Clean up the listener
  }, []);

  const formatData = (data) => {
    const formattedLogs = [];
    const appSet = new Set(); // To track unique applications for columns

    // Traverse the data and format it for the grid
    for (const category in data) {
      for (const machineId in data[category]) {
        for (const deploymentId in data[category][machineId]) {
          const deployment = data[category][machineId][deploymentId];

          // Ensure 'applications' exists and is an object
          const applications = deployment.applications || {};

          // Add application names to the appSet to track unique applications
          Object.keys(applications).forEach((appName) => appSet.add(appName));

          // Create a row for the deployment
          const deploymentRow = {
            id: `${machineId}-${deploymentId}`,
            machineId: machineId || "Unknown Machine", // Default to 'Unknown Machine' if machineId is missing
            deploymentId: deploymentId || "Unknown Deployment", // Default to 'Unknown Deployment' if deploymentId is missing
            deploymentStatus: deployment.status || "Unknown",
            startTime: deployment.startTime || "N/A",
          };

          // Add application statuses to the row
          for (const appName in applications) {
            deploymentRow[appName] = applications[appName];
          }

          formattedLogs.push(deploymentRow);
        }
      }
    }

    // Create columns for the DataGrid, including dynamically created app columns
    const appColumns = [
      { field: "machineId", headerName: "Machine ID", width: 150 },
      { field: "deploymentId", headerName: "Deployment ID", width: 200 },
      {
        field: "deploymentStatus",
        headerName: "Deployment Status",
        width: 150,
      },
      { field: "startTime", headerName: "Start Time", width: 200 },
    ];

    // Add a column for each unique application
    appSet.forEach((appName) => {
      appColumns.push({
        field: appName,
        headerName: appName.split("_").join(" "), // Replace underscores with spaces
        width: 150,
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <span
              style={{
                color: params.value === "Installed" ? zenzeroOrange : iceWhite,
              }}
            >
              {params.value === "Installed"
                ? "✔"
                : params.value === "Failed"
                  ? "✘"
                  : "-"}
            </span>
          </Box>
        ),
      });
    });

    return { formattedData: formattedLogs, appColumns };
  };

  // Filter logs based on search term
  const filteredLogs = logs.filter((log) =>
    Object.values(log).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  );

  const handleRowDoubleClick = (params) => {
    setSelectedMachineId(params.row.machineId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMachineId(null);
  };

  return (
    <>
      <div style={{ height: "calc(100vh - 200px)", width: "100%" }}>
        <StyledDataGrid
          rows={filteredLogs}
          columns={columns}
          pageSize={10}
          loading={loading}
          onRowDoubleClick={handleRowDoubleClick}
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: zenzeroColors.primary, // Use Zenzero primary color
            "& .MuiDataGrid-cell": {
              color: iceWhite,
              padding: "0px",
              paddingLeft: "10px",
              backgroundColor: Oceania,
              fontSize: "1em",
              fontWeight: "bold",
              alignItems: "center",
              alignContents: "center",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: zenzeroColors.primary, // Use Zenzero primary color
              fontWeight: "bold",
              marginBottom: "10px",
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: zenzeroOrange, // Use Zenzero primary color
              fontWeight: "bold",
              color: iceWhite,
            },
            "& .MuiTablePagination-root": {
              fontWeight: "bold",
              fontSize: "9",
              color: iceWhite,
              margin: 0,
              padding: 0,
            },
            "&.MuiToolbar-root": {
              padding: "0px",
              margin: "0px",
            },
            "&.MuiTablePagination-toolbar": {
              padding: "0px",
              margin: "0px",
            },
            "&.MuiToolbar-regular ": {
              padding: "0px",
              margin: "0px",
            },
            "& .MuiButton-root": {
              marginTop: "2px",
              textTransform: "none",
            },
          }}
        />
      </div>

      {/* Deployment Logs Dialog */}
      {openDialog && (
        <DeploymentLogsDialog
          clientCode="LMS" // Example value; replace with actual client code if needed
          machineName={selectedMachineId}
          open={openDialog}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
};

export default DeploymentLogsGrid;
